import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, QrCode, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Empty } from "@/components/admin/common";
import { api, formatErr } from "@/lib/api";

const MODES = [
  ["dynamic", "Dinamico", "Passa da salvilo.it: puoi cambiare la destinazione anche dopo la stampa e contare le scansioni."],
  ["static", "Diretto", "Contiene il link così com'è: non modificabile dopo la stampa, nessuna statistica."],
];

export const ClientSelect = ({ value, onChange, testId }) => {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
  }, []);
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="mt-1.5 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Nessun cliente</SelectItem>
        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const NewQrDialog = ({ open, onOpenChange, clientId = "" }) => {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", target_url: "", client_id: clientId, mode: "dynamic" });
  const submit = async () => {
    try {
      const { data } = await api.post("/qr-links", form);
      toast.success("QR creato");
      nav(`/admin/qr/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-qr-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">Nuovo QR da link</DialogTitle><DialogDescription>Un QR per qualsiasi indirizzo: sito, menu, recensioni, social, form.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Nome interno</Label><Input data-testid="qr-name-input" className="mt-1.5" placeholder="Es. Menu ristorante, Recensioni Google" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label className="text-xs">Link di destinazione</Label><Input data-testid="qr-target-input" className="mt-1.5" placeholder="https://" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} /></div>
          <div><Label className="text-xs">Cliente (opzionale)</Label><ClientSelect value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} testId="qr-client-select" /></div>
          <div className="grid gap-2">
            {MODES.map(([k, l, d]) => (
              <button key={k} type="button" data-testid={`qr-mode-${k}`} onClick={() => setForm({ ...form, mode: k })}
                className={`rounded-xl border p-3 text-left transition-colors ${form.mode === k ? "border-zinc-900 bg-zinc-50" : "hover:border-zinc-400"}`}>
                <div className="text-sm font-medium">{l}{k === "dynamic" && <span className="ml-2 text-[11px] text-terra">consigliato</span>}</div>
                <div className="text-[11px] text-zinc-500">{d}</div>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="qr-create-cancel">Annulla</Button><Button onClick={submit} disabled={!form.name || !form.target_url} data-testid="qr-create-submit">Crea QR</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function QrLinks() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/qr-links", { params: { q } }).then((r) => setItems(r.data));
  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const del = async (it) => {
    if (!window.confirm(`Eliminare "${it.name}"? I QR già stampati smetteranno di funzionare.`)) return;
    await api.delete(`/qr-links/${it.id}`);
    toast.success("QR eliminato");
    load();
  };
  return (
    <div>
      <PageHeader eyebrow="Strumenti" title="QR da link" description="Crea QR code per qualsiasi link, con colori e logo personalizzati.">
        <Button onClick={() => setOpen(true)} data-testid="add-qr-link-button"><Plus className="mr-1.5 h-4 w-4" />Nuovo QR</Button>
      </PageHeader>
      <div className="relative mb-5"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" /><Input data-testid="qr-search-input" placeholder="Cerca per nome, link o cliente" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 bg-white pl-9" /></div>
      {items && !items.length ? <Empty title="Nessun QR">Crea il primo QR da un link.</Empty> : (
        <QrLinkList items={items || []} onDelete={del} />
      )}
      <NewQrDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export const QrLinkList = ({ items, onDelete, testId = "qr-links-list" }) => {
  const nav = useNavigate();
  return (
        <div className="panel divide-y" data-testid={testId}>
          {items.map((it) => (
            <div key={it.id} className="flex cursor-pointer items-center gap-4 px-5 py-4 transition-colors hover:bg-zinc-50/70" onClick={() => nav(`/admin/qr/${it.id}`)} data-testid={`qr-link-row-${it.code}`}>
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-zinc-100"><QrCode className="h-5 w-5" strokeWidth={1.5} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="font-medium">{it.name}</span>{!it.active && <span className="text-[11px] text-zinc-400">disattivato</span>}</div>
                <div className="truncate text-xs text-zinc-500">{it.client_name ? `${it.client_name} · ` : ""}{it.target_url}</div>
              </div>
              <div className="hidden text-right text-xs text-zinc-500 sm:block">{it.mode === "dynamic" ? <><span className="tabular-nums text-zinc-900">{it.scans}</span> scansioni</> : "Diretto"}</div>
              <button onClick={(e) => { e.stopPropagation(); onDelete(it); }} className="p-2 text-zinc-400 hover:text-red-600" data-testid={`qr-link-delete-${it.code}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
  );
};
